import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'reactstrap';

import { getErrorMessage } from '~libs/axiosWrapper';
import { formatCurrency } from '~libs/dataUtils';

import { ErrorResponse, SummaryStatCard, SummaryStatCluster } from '~components';
import Icon, { faCheck, faFileInvoiceDollar, faMoneyBill, faUsers } from '~components/icon';

import { useGetPaymentBatchPreviewQuery } from '~direct-debits/actions/paymentBatches';
import { PaymentBatchPreviewQueryArgs } from '~direct-debits/models';

interface ComponentProps {
  value: { due_date_cutoff?: string };
  onChange?: (name: string, value: any) => void;
}

const DEBOUNCE_MS = 400;

// Three filters apply automatically — they're informational only, not user-
// toggleable. Mirrors the contract enforced server-side in
// `loadEligibleInvoices` / `previewBatch`. Rendered as green-checkmark
// indicators per the AC's "always-on, always-true" semantics.
const FILTER_LABELS = [
  'Unpaid only',
  'Valid mandate required',
  'Not in existing batch',
];

const Filters: React.FC<ComponentProps> = ({ value, onChange }) => {
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedArgs, setDebouncedArgs] = useState<PaymentBatchPreviewQueryArgs | null>({});

  const cutoff = value?.due_date_cutoff;

  // `onChange` identity is unstable across parent renders — depending on it
  // here would loop. The Formik setter behind it is stable enough, so it's
  // intentionally omitted from the deps in both effects below.
  useEffect(() => {
    // Clear any prior preview the moment the cutoff changes — otherwise
    // Step 2's `showNextButton` gate keeps reading the stale stats while the
    // debounced refetch is in flight.
    if (onChange) onChange('preview', undefined);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedArgs(cutoff ? { due_date_cutoff: cutoff } : {});
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [cutoff]);

  const { data, error, isFetching, isError } = useGetPaymentBatchPreviewQuery(debouncedArgs ?? {}, {
    skip: !debouncedArgs,
  });

  // Push successful previews into Formik; on error, clear any stale preview
  // so the gate can't be bypassed with leftover data.
  useEffect(() => {
    if (!onChange) return;
    if (data) {
      onChange('preview', data);
    } else if (isError) {
      onChange('preview', undefined);
    }
  }, [data, isError]);

  const stats = data?.stats;
  const hasResults = (stats?.invoice_count ?? 0) > 0;

  return (
    <div data-cy="payment-batches-wizard-filters">
      <Row className="mb-3">
        <Col>
          <div className="d-flex flex-wrap gap-3">
            {FILTER_LABELS.map((label) => (
              <div key={label} className="d-inline-flex align-items-center text-muted small">
                <Icon icon={faCheck} className="text-success me-2" />
                {label}
              </div>
            ))}
          </div>
        </Col>
      </Row>

      {/*
        SummaryStatCluster wraps its children in `d-flex flex-wrap gap-4`,
        which uses CSS `gap` rather than Bootstrap's --bs-gutter-x. That
        sidesteps the app-wide `.row { --bs-gutter-x: 0 }` override in
        styles/modules/_generic.scss that silently kills row gutters.
      */}
      <div className="mb-3">
        <SummaryStatCluster>
          <SummaryStatCard
            title="Clients"
            icon={<Icon icon={faUsers} />}
            count={<span>{stats?.client_count ?? 0}</span>}
            countClassName="text-primary"
            isLoading={isFetching}
          />
          <SummaryStatCard
            title="Invoices"
            icon={<Icon icon={faFileInvoiceDollar} />}
            count={<span>{stats?.invoice_count ?? 0}</span>}
            countClassName="text-primary"
            isLoading={isFetching}
          />
          <SummaryStatCard
            title="Total"
            icon={<Icon icon={faMoneyBill} />}
            count={<span>{formatCurrency(stats?.total_amount ?? 0)}</span>}
            countClassName="text-success"
            isLoading={isFetching}
          />
        </SummaryStatCluster>
      </div>

      {!isFetching && !isError && !hasResults && (
        <div className="text-muted small" data-cy="payment-batches-wizard-filters-empty">
          No eligible invoices match the current filters. Adjust the date cutoff above or check that clients have
          active mandates.
        </div>
      )}

      {isError && <ErrorResponse message={getErrorMessage(error)} />}
    </div>
  );
};

export default Filters;
